// Public deck-building methodology page. Sister doc to /technical-faq:
// where that page covers the engineering stack, this one covers the
// agent's reasoning rules — the named pro principles that constrain
// HOW the agent thinks about decks. Linked from /technical-faq's
// "Prompt engineering" section and from the landing-page FAQ.
//
// Sourced from Plans/PRO-DECK-BUILDING.md (the internal synthesis of a
// 7-domain research pass) but reframed: the internal doc is a research
// log; this page is a positive manifesto. No iteration history, no
// graveyards, no in-flight notes. Just "here's how the agent reasons,
// here are the pros whose canon shaped that reasoning, here's why it
// matters for the recommendations you see."

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSeoHead } from "../lib/useSeoHead";

const PAGE_TITLE =
  "How the Oracle reasons about decks — pro principles, rule by rule | Deck-building method";
const PAGE_DESCRIPTION =
  "The Oracle's deck-building agent is constrained by named pro principles: Mike Flores's 'Misassignment of Role = Game Loss', Reid Duke's 'every card needs a job' and 'Band-Aid' anti-pattern, PVDDR's potential-vs-realized power framework, Frank Karsten's mana-base regression, and the Limited Resources UBER/CABS doctrine. Each rule is encoded in a versioned system prompt, gated by an A/B eval harness, and traceable from a deck recommendation back to the named pro source.";
const PAGE_URL = "https://themtgoracle.com/deck-building-method";

const PAGE_JSONLD: Array<Record<string, unknown>> = [
  {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline:
      "How the MTG Oracle reasons about decks — pro principles, rule by rule",
    description: PAGE_DESCRIPTION,
    url: PAGE_URL,
    mainEntityOfPage: PAGE_URL,
    inLanguage: "en-US",
    author: { "@type": "Organization", name: "MTG Oracle" },
    publisher: {
      "@type": "Organization",
      name: "MTG Oracle",
      logo: {
        "@type": "ImageObject",
        url: "https://themtgoracle.com/logo.svg",
      },
    },
  },
];

export default function DeckBuildingMethod() {
  useSeoHead({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonical: PAGE_URL,
    jsonLd: PAGE_JSONLD,
  });
  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <header className="border-b border-border/60">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="MTG Oracle" width={28} height={28} />
            <span className="font-semibold tracking-tight">MTG Oracle</span>
          </Link>
          <Link to="/" className="btn-ghost text-sm inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium text-accent mb-3 uppercase tracking-wider">
          Deck-building method
        </p>
        <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.1]">
          How the Oracle reasons about decks
        </h1>
        <p className="mt-6 text-lg text-fg-muted leading-relaxed">
          A chatbot dump treats &ldquo;build me a deck&rdquo; as a writing
          task. The Oracle treats it as a constrained reasoning task,
          gated by named pro principles encoded into the agent's system
          prompt. The principles below are the actual rules the agent
          checks itself against before it ships a recommendation. Each
          one traces back to a named pro source, and each one was added
          because removing it produced measurably worse decks in our
          eval harness.
        </p>

        <div className="mt-12 space-y-10 text-sm md:text-base text-fg-muted leading-relaxed">

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              The unifying thesis: a deck has a plan; a pile of cards doesn't
            </h2>
            <p className="mt-3">
              Every named pro doctrine the Oracle follows reduces to one
              shape:{" "}
              <strong className="text-fg">
                a deck has a one-sentence mission statement, and every
                slot earns its place by advancing that statement.
              </strong>{" "}
              Gavin Verhey, on Wizards' own blog, put it most plainly:{" "}
              <em>
                &ldquo;A deck has a strategy. A goal. A plan. A pile of
                cards, on the other hand, can be incoherent.&rdquo;
              </em>{" "}
              Reid Duke's discipline test runs on the same axis:{" "}
              <em>
                &ldquo;Identify the game plan of your deck and build with
                that in mind. Don't include cards just because they're fun
                or exciting if they aren't really contributing to what
                you're trying to do.&rdquo;
              </em>{" "}
              Mike Flores's 1999 doctrine{" "}
              <em>&ldquo;Misassignment of Role = Game Loss&rdquo;</em>{" "}
              is the same theorem stated as a failure mode.
            </p>
            <p className="mt-3">
              The Oracle bakes that thesis into a structural rule: before
              writing any analysis, it commits to a concrete kill plan in
              the Win Condition section — which cards close, on which
              turn, and how the lines interact. Every other section
              (Key Interactions, Sideboard Guide, Weaknesses, Honorable
              Mentions) must then be{" "}
              <strong className="text-fg">consistent</strong> with that
              kill plan. A &ldquo;cute synergy&rdquo; that doesn't connect
              to how the deck wins gets cut. A &ldquo;weakness&rdquo; that
              names a matchup the kill plan was never designed against is
              a strawman. A rejected card whose dismissal contradicts the
              kill plan signals that the rejection logic is wrong, or the
              kill plan was misstated.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Closers are not the sum of their parts
            </h2>
            <p className="mt-3">
              The Oracle treats top-end finishers as{" "}
              <strong className="text-fg">single resolved cards</strong>,
              not as parts lists. This rule exists because the most
              common AI deck-building failure is gestalt loss under
              decomposition: a 7-mana spell that wins games gets
              rejected because &ldquo;X is a faster clock and Y is
              cheaper card draw, so we don't need it.&rdquo; The
              substitution math is locally correct but role-wrong.
            </p>
            <p className="mt-3">
              Mike Flores named the underlying confusion in{" "}
              <em>The Philosophy of Fire</em>: closers are evaluated on a
              cards-vs-life ledger, not a cards-vs-cards ledger.{" "}
              <em>
                &ldquo;Though it looks like you are falling behind when
                the Skizzik hits the bin, in fact that one card
                over-performed according to your units of
                measurement.&rdquo;
              </em>{" "}
              A single resolved 7-mana spell forces the opponent to
              answer all of its effects with one removal beat. Two
              cheaper cards that produce the same effects across two
              casts give the opponent two answer windows. The gestalt is
              the compression.
            </p>
            <p className="mt-3">
              When the agent rejects a top-end finisher, it has to give
              a role-coherent reason: a strictly-better closer in the
              same role; the deck's curve cannot reliably reach the
              cost; or — the aggro escape clause — the deck plans to
              win before the card's turn (&ldquo;this deck wins by turn
              5; a turn-7 spell arrives after the game is over&rdquo;).
              Component decomposition and cross-role mana-cost
              comparisons are forbidden as rejection forms.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              UBER vs CABS: commit to one
            </h2>
            <p className="mt-3">
              Limited Resources' canonical taxonomy maps cleanly onto
              Constructed deck-building.{" "}
              <strong className="text-fg">UBER decks</strong> are
              organized around a late-game haymaker that's hard to
              remove.{" "}
              <strong className="text-fg">CABS decks</strong>{" "}
              (Curve / Aggressive / Board-Stabilizing) win through tempo
              and presence. The Oracle picks one at the outset and
              commits — a halfway deck has neither the haymaker UBER
              provides nor the curve CABS provides.
            </p>
            <p className="mt-3">
              The most common AI failure mode is the structural-failure
              halfway state: replacing a 7-mana finisher with a 5-mana
              flier and a 3-mana cantrip. The new shell looks like
              progress on every axis until it's played, at which point
              it's neither aggressive enough to race nor durable enough
              to stabilize. Picking UBER or CABS first, then
              card-by-card committing, is how pros avoid the trap.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Reactive cards must lead to winning, not just surviving longer
            </h2>
            <p className="mt-3">
              Reid Duke named this anti-pattern by hand:{" "}
              <em>&ldquo;Band-Aid cards.&rdquo;</em>{" "}
              From{" "}
              <em>High-Impact Cards</em> on Star City Games:{" "}
              <em>
                &ldquo;Sure, as I'm playing there will be many situations
                where I feel like I need a Band-Aid — 'If only I could
                topdeck Toxic Deluge here!' However, such a card would
                only allow me to survive longer; it would not actually
                lead to me winning the game.&rdquo;
              </em>
            </p>
            <p className="mt-3">
              For every reactive slot — spot removal, sweepers,
              counterspells, lifegain — the agent has to answer a binary
              question:{" "}
              <strong className="text-fg">
                does this card lead to winning, or only to surviving
                longer?
              </strong>{" "}
              If the answer is &ldquo;surviving longer,&rdquo; the card
              stays only when there's a separate slot in the deck that
              closes the game when survival is achieved. Otherwise the
              card is a Band-Aid: it covers the immediate problem
              without advancing the kill plan, and the deck plays into
              the same losing pattern one turn later.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Mana bases are not negotiable
            </h2>
            <p className="mt-3">
              Mana-base math is the most-skipped axis in AI deck
              recommendations and the one with the sharpest competitive
              cost. Frank Karsten's regression-based formulas are the
              canonical reference, and the Oracle treats them as a
              prior, not as a starting point that gets averaged against
              other published lists.{" "}
              <em>
                &ldquo;Number of lands ≈ 19.59 + 1.90 × average mana
                value − 0.28 × cantrip/ramp count + 0.27 × companion
                count.&rdquo;
              </em>{" "}
              R² = 0.395, RMSE = 2.75. Karsten's own caveat:{" "}
              <em>&ldquo;Not great, not terrible.&rdquo;</em>
            </p>
            <p className="mt-3">
              The colored-source side is sharper. Karsten targets 89+M%
              consistency where M is mana value:{" "}
              <em>
                &ldquo;90 percent consistency for one-mana cards, 91
                percent for two-mana, 92 percent for three-mana &hellip;
                up to 96 percent for seven-mana.&rdquo;
              </em>{" "}
              The Oracle splits color-source counts into{" "}
              <strong className="text-fg">turn-1 untapped sources</strong>{" "}
              (for one-drops) and{" "}
              <strong className="text-fg">turn-2+ all sources</strong>{" "}
              (for everything else), tracks the double-pip cliff (going
              from {"{R}"} to {"{R}{R}"} at the same CMC adds about six
              sources to the requirement), and applies asymmetric
              weights to MDFCs (~⅔ of a land for total-land math, but
              ~80&ndash;100% of a source for color math, since the spell
              side dilutes opening-hand land utility but doesn't dilute
              color access once you have other lands).
            </p>
            <p className="mt-3">
              When the Oracle splashes a third or fourth color, it
              names the highest-pip cost in that color, the source
              count it's committing to, and which Karsten tier the
              count satisfies. Sam Black's rule —{" "}
              <em>
                &ldquo;The more aggressive your deck is, the less you
                should be willing to splash. If your deck is expecting
                to play longer games, you can splash much more
                freely&rdquo;
              </em>{" "}
              — gates whether splashing is even on the table.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Best-in-format ≠ best-for-this-tournament
            </h2>
            <p className="mt-3">
              When the user asks for &ldquo;the best deck,&rdquo; the
              first move is disambiguation. Paulo Vitor Damo da Rosa's
              <em> potential-vs-realized power</em> framework is the
              authoritative resolution of the &ldquo;play the best
              deck&rdquo; vs &ldquo;metagame call&rdquo; debate:{" "}
              <em>
                &ldquo;It doesn't matter which deck has the most
                potential (i.e. which deck is the theoretically
                strongest in a vacuum), because when you sign up to
                play a tournament, you don't play with the deck in
                perfect conditions — what it matters is what number you
                can get to in that specific tournament.&rdquo;
              </em>
            </p>
            <p className="mt-3">
              His helicopter analogy:{" "}
              <em>
                &ldquo;It doesn't help to know that the fastest way to a
                place is by helicopter, because you don't know how to
                fly a helicopter — so the fastest way for you is to
                drive a car as opposed to taking the bus.&rdquo;
              </em>{" "}
              And his override threshold:{" "}
              <em>
                &ldquo;Sometimes the deck you're considering isn't
                actually the third-best deck, but the eleventh-best
                deck, in which case it's probably too weak that no
                amount of familiarity will save you &hellip; as a general
                rule, if you're the only person playing that deck, it
                probably doesn't clear the bar.&rdquo;
              </em>
            </p>
            <p className="mt-3">
              When proposing a &ldquo;preying on X&rdquo; deck, the
              Oracle has to name the X it preys on, the population it
              targets (and the share of the field that population must
              occupy for the bet to clear), and the decks that beat the
              meta-call deck. Without those three, it's a flavor pick
              dressed up as a meta call — exactly the failure mode pros
              flag.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Sideboard guides are kill-plan-protected
            </h2>
            <p className="mt-3">
              Reid Duke is{" "}
              <em>
                &ldquo;cautious about transformational sideboards&rdquo;
              </em>{" "}
              and treats focused 2-of and 3-of tuning swaps as the
              normal case:{" "}
              <em>
                &ldquo;Transform sideboards can be fun and impressive
                when they work out, but I recommend turning to them
                only as a hail mary when nothing else is working. After
                all, sideboarding should be about perfecting your deck,
                not about clumsily trying to employ two conflicting
                strategies at the same time.&rdquo;
              </em>
            </p>
            <p className="mt-3">
              Beyond that default, the Oracle's sideboard guides are
              constrained by{" "}
              <strong className="text-fg">kill-plan protection</strong>:
              cards named in the deck's stated kill plan (the named
              closer, lock-piece, key threat, or core engine card)
              cannot be cut in a sideboard swap unless the same bullet
              either (i) cites a matchup-specific reframe of the deck's
              plan (&ldquo;vs aggro we shift to attrition mode,
              sweepers replace top-end&rdquo;) or (ii) explicitly flags
              a transformation and names the replacement closer.
              Generic phrases like &ldquo;too slow against fast
              decks&rdquo; or &ldquo;grindy matchups want different
              cards&rdquo; don't count — they're vague abandonments,
              not matchup-specific reasoning.
            </p>
            <p className="mt-3">
              And after Frank Karsten's match-win-percentage doctrine:
              sideboard slots are allocated to maximize{" "}
              <em>
                expected match-win percentage across the expected
                metagame
              </em>
              , not the count of favorable matchups. Beating one bad
              matchup at 50% field share is worth more than beating
              three bad matchups at 5% each. Brian Braun-Duin's{" "}
              <em>dartboard</em> anti-pattern — swapping cards for
              similar-power cards because both are tagged
              &ldquo;good&rdquo; — is the inverse failure the Oracle
              actively rejects.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Card counts are mulligan-aware
            </h2>
            <p className="mt-3">
              A 1-of in a 60-card deck shows up in your opener about
              12% of the time. That number is the prior the Oracle
              uses when accepting or rejecting any 1-of inclusion. A
              1-of is justified when at least one of three things is
              true: there's a tutor or card-selection engine in the
              same deck that can find it; drawing a second copy is
              actively bad (legend rule, planeswalker collision,
              hard-coded anti-synergy); or the card is a fine top-deck
              even if you never expected it (acceptable floor).
              Without one of those, a 1-of is a count bug, not a
              design choice — pros' shorthand:{" "}
              <em>
                &ldquo;If you don't have a tutor and you wouldn't be
                sad to draw two, you don't have a 1-of, you have a
                card-count bug.&rdquo;
              </em>
            </p>
            <p className="mt-3">
              Frank Karsten's London-mulligan analysis reshaped the
              redundancy budget:{" "}
              <em>
                &ldquo;The probability of finding an opening hand that
                guarantees a turn-3 Karn Liberated or Wurmcoil Engine
                goes from 16% under the Vancouver mulligan rule to 33%
                under the London mulligan rule.&rdquo;
              </em>{" "}
              That's why post-London, 3-of hate cards usually replace
              4-ofs and tutors get better. The Oracle's count
              recommendations bake in those probabilities.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              How these rules become recommendations
            </h2>
            <p className="mt-3">
              Each of the principles above is encoded as a numbered
              rule in the agent's versioned system prompt. Adding,
              changing, or removing a rule produces a new prompt
              version, and every version is gated by an A/B eval
              harness against a curated set of deck-building briefs
              before it can become the default.
            </p>
            <p className="mt-3">
              The eval has two independent graders. A{" "}
              <strong className="text-fg">depth grader</strong>{" "}
              scores each section of every answer on a 1&ndash;5
              rubric. A{" "}
              <strong className="text-fg">boolean criterion grader</strong>{" "}
              checks specific rule-compliance claims per section
              (e.g.{" "}
              <em>
                &ldquo;does every Key Interactions bullet advance the
                stated kill plan?&rdquo;
              </em>
              ,{" "}
              <em>
                &ldquo;does the Sideboard Guide cut any kill-plan card
                without flagging a transformation?&rdquo;
              </em>
              ). A new prompt version has to win on the boolean axis
              that targets the specific rule it added, without
              regressing on any other axis by more than a small
              tolerance. Versions that fail the gate get tombstoned in
              the codebase as retro-evaluation references.
            </p>
            <p className="mt-3">
              The result: a deck recommendation you see in the Oracle
              is the output of a system that explicitly checks itself
              against named pro principles before it ships, and every
              principle is traceable from the recommendation back to a
              quoted source. Not all of pro deck-building wisdom is
              in here yet — every rule on this page is a single rule
              we measured. The list grows on the same A/B-gated cadence.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Sources
            </h2>
            <ul className="mt-3 list-disc list-inside space-y-1.5">
              <li>
                Mike Flores —{" "}
                <a
                  href="https://articles.starcitygames.com/articles/whos-the-beatdown"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>Who's the Beatdown?</em>
                </a>{" "}
                and{" "}
                <a
                  href="https://articles.starcitygames.com/articles/the-philosophy-of-fire"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>The Philosophy of Fire</em>
                </a>
              </li>
              <li>
                Reid Duke —{" "}
                <a
                  href="https://articles.starcitygames.com/articles/streamlining-your-deck/"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>Streamlining Your Deck</em>
                </a>
                ,{" "}
                <a
                  href="https://articles.starcitygames.com/articles/high-impact-cards/"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>High-Impact Cards</em>
                </a>
                , Level One series (
                <a
                  href="https://magic.wizards.com/en/articles/archive/level-one/sideboard-2015-08-10"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Sideboard
                </a>
                ,{" "}
                <a
                  href="https://magic.wizards.com/en/articles/archive/level-one/building-mana-base-2014-11-24"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Building a Mana Base
                </a>
                ,{" "}
                <a
                  href="https://magic.wizards.com/en/articles/archive/level-one/mulligans-2015-01-26"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mulligans
                </a>
                )
              </li>
              <li>
                Paulo Vitor Damo da Rosa —{" "}
                <a
                  href="https://threeforonetrading.com/en/experience-vs-meta"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>Should You Play What Is Best Or What You Know</em>
                </a>{" "}
                and his Channel Fireball sideboarding writing
              </li>
              <li>
                Frank Karsten —{" "}
                <a
                  href="https://www.peasant-magic.com/articles/magic-deckbuilding/how-many-lands-do-you-need-in-your-deck-an-updated-analysis"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>How Many Lands Do You Need&hellip; (2022)</em>
                </a>
                ,{" "}
                <a
                  href="https://www.peasant-magic.com/articles/magic-deckbuilding/how-many-sources-do-you-need-to-consistently-cast-your-spells"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>How Many Sources&hellip; (2022)</em>
                </a>
                , and his London-mulligan probability analysis
              </li>
              <li>
                Gavin Verhey —{" "}
                <a
                  href="https://magic.wizards.com/en/articles/archive/beyond-basics/my-most-important-deck-building-rule-2018-02-08"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>My Most Important Deck-Building Rule</em>
                </a>
              </li>
              <li>
                Chad Ellis —{" "}
                <a
                  href="https://articles.starcitygames.com/articles/the-danger-of-cool-things"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>The Danger of Cool Things</em>
                </a>
              </li>
              <li>
                Limited Resources (Marshall Sutcliffe + LSV) —{" "}
                <a
                  href="https://lrcast.com/limited-resources-301-uber-theory-and-exclusive-bfz-preview-cards/"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  UBER Theory (LR 301)
                </a>{" "}
                and{" "}
                <a
                  href="https://lrcast.com/limited-resources-486-arena-check-in-m19-and-cabs-theory/"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CABS Theory (LR 486)
                </a>
              </li>
              <li>
                Sam Black —{" "}
                <a
                  href="https://articles.starcitygames.com/magic-the-gathering/premium/when-and-how-to-pivot-or-stretch-your-mana-in-innistrad-midnight-hunt-limited/"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>When And How To Pivot Or Stretch Your Mana</em>
                </a>
              </li>
              <li>
                Brian Braun-Duin, Bob Huang, and Drake Sasser — Channel
                Fireball / Hipsters of the Coast (sideboard
                anti-patterns and Modern's RPS structure)
              </li>
            </ul>
          </section>

          <section>
            <p className="text-sm text-fg-muted">
              For the engineering side of how the agent runs (model
              routing, retrieval pipeline, eval harness),{" "}
              <Link
                to="/technical-faq"
                className="text-accent hover:underline"
              >
                read the technical deep dive
              </Link>
              .
            </p>
          </section>

        </div>
      </main>
    </div>
  );
}
